/**
 * Phase 27.50.B.11 — Delete-with-reason dialog (KR admin only).
 *
 * Shared modal used by the 3 delete flows (Marketing / Event / Retail).
 * Presents 8 curated reason presets that seed a free-text field so the
 * audit log tells the *story*, not just the action. The consumer wires
 * this in place of the previous `window.confirm` calls.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <DeleteRequestDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     resourceLabel="marketing request"
 *     summary="Postcard flyer — testinvite"
 *     onConfirm={async ({ reason, reasonPreset }) => { ... }}
 *   />
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';

export const DELETION_REASON_PRESETS = [
  { value: 'duplicate', label: 'Duplicate entry', text: 'Duplicate — same request already exists.' },
  { value: 'test', label: 'Test / demo entry', text: 'Test entry — created for QA / demo purposes.' },
  { value: 'wrong_info', label: 'Incorrect information', text: 'Wrong information — agent will resubmit with the correct details.' },
  { value: 'submitted_by_mistake', label: 'Submitted by mistake', text: 'Submitted by mistake — cleaning up on the agent’s behalf.' },
  { value: 'agent_request', label: 'Requested by agent', text: 'Deletion requested directly by the agent.' },
  { value: 'superseded', label: 'Superseded by newer request', text: 'A newer request replaces this one.' },
  { value: 'spam', label: 'Spam / abuse', text: 'Spam or abusive entry — removed.' },
  { value: 'other', label: 'Other (write your own)', text: '' },
];

export default function DeleteRequestDialog({
  open, onOpenChange,
  resourceLabel = 'request',
  summary,
  onConfirm,
}) {
  const [reasonPreset, setReasonPreset] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset when the dialog opens so we never carry state across items.
  useEffect(() => {
    if (open) {
      setReasonPreset('');
      setReason('');
      setBusy(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!reasonPreset) return;
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim(), reasonPreset });
      onOpenChange(false);
    } catch {
      // parent handles the error toast
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle size={18} /> Delete {resourceLabel}?
          </DialogTitle>
          <DialogDescription>
            This action is destructive but recoverable from the Deletion Log for 90 days.
          </DialogDescription>
        </DialogHeader>

        {summary && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {summary}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Reason (required)
            </Label>
            <Select
              value={reasonPreset}
              onValueChange={(val) => {
                const preset = DELETION_REASON_PRESETS.find((p) => p.value === val);
                setReasonPreset(val);
                setReason(preset ? preset.text : '');
              }}
            >
              <SelectTrigger className="mt-1" data-testid="delete-reason-preset">
                <SelectValue placeholder="Choose a reason…" />
              </SelectTrigger>
              <SelectContent>
                {DELETION_REASON_PRESETS.map((p) => (
                  <SelectItem
                    key={p.value}
                    value={p.value}
                    data-testid={`delete-reason-preset-${p.value}`}
                  >
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Notes
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Add specifics for the audit log…"
              className="mt-1"
              data-testid="delete-reason-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reasonPreset || busy}
            data-testid="delete-request-confirm"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {busy ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Trash2 size={14} className="mr-1.5" />
            )}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
